import React, { useState, useMemo, useEffect, useContext } from 'react';
//@ts-ignore
import DateInput from '../DateInput/DateInput';
import { CoverLevel, ProductType } from '../CommonTypes';
import {
  getDefaultClaimLimit,
  getDefaultDuration,
  getDefaultPaymentMethod,
  getDefaultVariant,
  getSelectedVariant,
  getUniqueTyreTypes,
  getUniqueClaimLimits,
  getUniqueCoverLevels,
  getUniqueDurations,
  getUniquePaymentMethods,
  ClaimLimit,
  validateVariablePrice,
  getVariantTotalPrice
} from '../ProductMethods';
import { PaymentMethod } from '../Constants';
import PriceDisplay from '../PriceDisplay';
import { ButtonCommand } from '../Button/ButtonCommand';
import PaymentMethods from './PaymentMethods';
import RadioSelection from './RadioSelection';
import PriceInput from '../PriceInput/PriceInput';
import { getCurrencySymbol } from '../Formatter';
import { CommonValidation } from '../CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { ProductTypes } from '../../../consts/Common';
import { SiteContext } from '../../../context/SiteContext';
import ShowBenefitsModal from '../ShowBenefitsModal';

export enum ProductFormStatus {
  Idle,
  ValidatingSaveQuote,
  ValidatingCheckout
}

export type ProductSelectionProps = {
  product: ProductType;
  noOfInstallments: number;
  paymentOptions: boolean;
  isQuote: boolean;
  addOrRemoveProduct: (id: number) => void;
  onChangePolicyStartDate(productTypeId: number, dateVal: string | null): void;
  onProductSelectionChange(
    productTypeId: number,
    tyreTypeId: number | undefined,
    claimLimit: number | undefined,
    coverLevelId: number | undefined,
    paymentMethod: string | undefined,
    variantId: string
  ): void;
  onUpdateVariantPrice(
    productTypeId: number,
    priceValue: number | undefined
  ): void;
  onPriceFocus: () => void;
  vehicleDeliveryDate: string;
  showPriceField: boolean;
  changeTotalPriceOnVariantChange: boolean;
  productFormStatus: ProductFormStatus;
  onValidated: (productTypeId: number, isValid: boolean) => void;
  exceedsUpsellMileageLimit?: boolean;
  culture: string;
  hideRemovePlanLink?: boolean;
  paymentProfileLabel?: string;
};

export type ProductSelectionError = {
  policyStartDateError: string;
  paymentMethodNotSelectedError: string;
  variantNotSelectedError: string;
  variablePriceError: string;
  coverLevelNotSelectedError: string;
  tyreTypeNotSelectedError: string;
  claimLimitNotSelectedError: string;
};

const initialProductErrors = {
  paymentMethodNotSelectedError: '',
  policyStartDateError: '',
  variantNotSelectedError: '',
  variablePriceError: '',
  coverLevelNotSelectedError: '',
  tyreTypeNotSelectedError: '',
  claimLimitNotSelectedError: ''
};

const ProductSelection = ({
  product,
  noOfInstallments,
  paymentOptions,
  isQuote,
  addOrRemoveProduct,
  onProductSelectionChange,
  onChangePolicyStartDate,
  onUpdateVariantPrice,
  onPriceFocus,
  vehicleDeliveryDate,
  showPriceField,
  changeTotalPriceOnVariantChange,
  productFormStatus,
  onValidated,
  exceedsUpsellMileageLimit,
  culture,
  hideRemovePlanLink,
  paymentProfileLabel
}: ProductSelectionProps): JSX.Element => {
  const [productErrors, setProductErrors] =
    useState<ProductSelectionError>(initialProductErrors);
  const selectedVariant = getSelectedVariant(product.productVariants);
  const selectedVariantTotalPrice = getVariantTotalPrice(
    product.isVariablePrice,
    selectedVariant
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const { site } = useContext(SiteContext);
  const cmsProduct = site.content.productPage;

  const onShowBenefits = () => {
    setShowModal(true);
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const [totalPrice, setTotalPrice] = useState<string>(
    selectedVariantTotalPrice?.toString() ?? ''
  );
  const isSubscription =
    product.selectedPaymentMethod === PaymentMethod.Subscription ||
    product.readOnlyPolicyStartDate;
  const tyreTypes = getUniqueTyreTypes(product.productVariants);
  const claimLimits = getUniqueClaimLimits(
    product.productVariants,
    product.selectedTyreTypeId
  );
  const coverLevels = getUniqueCoverLevels(product.productVariants);

  const paymentMethods = exceedsUpsellMileageLimit
    ? [PaymentMethod.Single]
    : getUniquePaymentMethods(
        product.productVariants,
        product.selectedTyreTypeId,
        product.selectedClaimLimit,
        product.selectedCoverLevelId
      );
  const durations = getUniqueDurations(
    product.productVariants,
    product.selectedTyreTypeId,
    product.selectedClaimLimit,
    product.selectedCoverLevelId,
    product.selectedPaymentMethod
  );

  const currencySymbol = getCurrencySymbol();

  const onSelectionChange = (
    productTypeId: number,
    tyreTypeId: number | undefined,
    claimLimit: number | undefined,
    coverLevelId: number | undefined,
    paymentMethod: string | undefined,
    variantId: string
  ) => {
    const resetErrors = { ...productErrors };

    if (tyreTypeId) {
      resetErrors.tyreTypeNotSelectedError = '';
    }

    if (claimLimit) {
      resetErrors.claimLimitNotSelectedError = '';
    }

    if (coverLevelId) {
      resetErrors.coverLevelNotSelectedError = '';
    }

    if (paymentMethod) {
      resetErrors.paymentMethodNotSelectedError = '';
    }

    if (variantId) {
      resetErrors.variantNotSelectedError = '';
    }

    setProductErrors(resetErrors);

    onProductSelectionChange(
      productTypeId,
      tyreTypeId,
      claimLimit,
      coverLevelId,
      paymentMethod,
      variantId
    );
  };

  const updateTyreType = (tyreTypeId: number) => {
    const defaultClaimLimit = getDefaultClaimLimit(
      product.productVariants,
      tyreTypeId
    );
    const defaultPaymentMethod = getDefaultPaymentMethod(
      product.productVariants,
      tyreTypeId,
      defaultClaimLimit?.value,
      product.selectedCoverLevelId
    );
    const defaultDuration = getDefaultDuration(
      product.productVariants,
      tyreTypeId,
      defaultClaimLimit?.value,
      product.selectedCoverLevelId,
      defaultPaymentMethod
    );
    const defaultVariant = getDefaultVariant(
      product.productVariants,
      tyreTypeId,
      defaultClaimLimit?.value,
      product.selectedCoverLevelId,
      defaultPaymentMethod,
      defaultDuration
    );

    onSelectionChange(
      product.productTypeId,
      tyreTypeId,
      defaultClaimLimit?.value,
      product.selectedCoverLevelId,
      defaultPaymentMethod,
      defaultVariant?.variantId || ''
    );
  };

  const updateClaimLimit = (claimLimit: ClaimLimit) => {
    const defaultPaymentMethod = getDefaultPaymentMethod(
      product.productVariants,
      product.selectedTyreTypeId,
      claimLimit.value,
      product.selectedCoverLevelId
    );
    const defaultDuration = getDefaultDuration(
      product.productVariants,
      product.selectedTyreTypeId,
      claimLimit.value,
      product.selectedCoverLevelId,
      defaultPaymentMethod
    );
    const defaultVariant = getDefaultVariant(
      product.productVariants,
      product.selectedTyreTypeId,
      claimLimit.value,
      product.selectedCoverLevelId,
      defaultPaymentMethod,
      defaultDuration
    );

    onSelectionChange(
      product.productTypeId,
      product.selectedTyreTypeId,
      claimLimit.value,
      product.selectedCoverLevelId,
      defaultPaymentMethod,
      defaultVariant?.variantId || ''
    );
  };

  const { t } = useTranslation();

  const totalPriceError = t('customer-product-page.total-price-empty-error');

  const updateCoverLevel = (coverLevel: CoverLevel) => {
    const defaultPaymentMethod = getDefaultPaymentMethod(
      product.productVariants,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      coverLevel.value
    );
    const defaultDuration = getDefaultDuration(
      product.productVariants,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      coverLevel.value,
      defaultPaymentMethod
    );
    const defaultVariant = getDefaultVariant(
      product.productVariants,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      coverLevel.value,
      defaultPaymentMethod,
      defaultDuration
    );

    onSelectionChange(
      product.productTypeId,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      coverLevel.value,
      defaultPaymentMethod,
      defaultVariant?.variantId || ''
    );
  };

  const updatePaymentMethod = (paymentMethod: string) => {
    const defaultDuration = getDefaultDuration(
      product.productVariants,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      product.selectedCoverLevelId,
      paymentMethod
    );

    const defaultVariant = getDefaultVariant(
      product.productVariants,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      product.selectedCoverLevelId,
      paymentMethod,
      defaultDuration
    );

    onSelectionChange(
      product.productTypeId,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      product.selectedCoverLevelId,
      paymentMethod,
      defaultVariant?.variantId || ''
    );
  };

  const updateDuration = (duration: string) => {
    const defaultVariant = getDefaultVariant(
      product.productVariants,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      product.selectedCoverLevelId,
      product.selectedPaymentMethod,
      duration
    );

    onSelectionChange(
      product.productTypeId,
      product.selectedTyreTypeId,
      product.selectedClaimLimit,
      product.selectedCoverLevelId,
      product.selectedPaymentMethod,
      defaultVariant?.variantId || ''
    );
  };

  const addOrRemovePlan = () => {
    addOrRemoveProduct(product.productTypeId);
  };

  const onDurationChange = (duration: string) => {
    updateDuration(duration);
  };

  const onPaymentMethodClick = (paymentMethod: string) => {
    updatePaymentMethod(paymentMethod);
  };

  const onChangePolicyStartDateValue = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    onChangePolicyStartDate(
      product.productTypeId,
      (e.target as HTMLInputElement).value
    );
  };

  useEffect(() => {
    if (product.readOnlyPolicyStartDate) return;

    if (isSubscription) {
      onChangePolicyStartDate(product.productTypeId, getJSONDate());
    } else {
      onChangePolicyStartDate(product.productTypeId, null);
    }
  }, [product.selectedPaymentMethod]);

  useEffect(() => {
    if (productFormStatus === ProductFormStatus.ValidatingCheckout) {
      validateProduct(true);
    } else if (productFormStatus === ProductFormStatus.ValidatingSaveQuote) {
      validateProduct(false);
    }
  }, [productFormStatus]);

  useEffect(() => {
    if (
      changeTotalPriceOnVariantChange ||
      !product.isVariablePrice ||
      isSubscription
    ) {
      if (!isSubscription) {
        setTotalPrice(selectedVariantTotalPrice?.toString() ?? '');
      }
      setProductErrors({
        ...productErrors,
        policyStartDateError: '',
        variablePriceError: ''
      });
    } else if (selectedVariant && totalPrice) {
      updateVariantTotalPrice(totalPrice);
    }
  }, [selectedVariant?.variantId]);

  const getJSONDate = () => {
    const dateObject = {
      value: new Date().toISOString(),
      utc: '0'
    };

    const json = JSON.stringify(dateObject);
    return json;
  };

  const onChangeTotalPrice = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    let cleanValue = value;
    if (value.endsWith('.')) {
      cleanValue = value.slice(0, -1);
    }

    if (!isNaN(Number(cleanValue)) && (value.match(/[.]/g) || []).length <= 1) {
      setTotalPrice(value);
    }

    if (productErrors?.variablePriceError !== '') {
      setProductErrors({ ...productErrors, variablePriceError: '' });
    }
  };

  const onLeaveTotalPrice = (e: React.FormEvent<HTMLInputElement>) => {
    const valueString = (e.target as HTMLInputElement).value;
    updateVariantTotalPrice(valueString);
  };

  const getTotalPriceValue = (valueString: string): number | undefined => {
    if (
      CommonValidation.isEmpty(valueString) ||
      isNaN(parseFloat(valueString))
    ) {
      return undefined;
    } else {
      return parseFloat(valueString);
    }
  };

  const updateVariantTotalPrice = (valueString: string) => {
    const value = getTotalPriceValue(valueString);
    if (value === undefined) {
      setTotalPrice('');
    }
    validateTotalPrice(
      value,
      selectedVariant?.minimumPrice ?? 0,
      selectedVariant?.maximumPrice ?? 0
    );
    onUpdateVariantPrice(product.productTypeId, value);
  };

  const fontDisabledClass = !product.selectedPaymentMethod
    ? 'font-disabled'
    : '';

  const validateTotalPrice = (
    value: number | undefined,
    minimumValue: number,
    maximumValue: number
  ) => {
    let errorMessage = '';

    if (!value) {
      errorMessage = totalPriceError;
    } else if (value < minimumValue || value > maximumValue) {
      errorMessage = t('customer-product-page.price-invalid-error');
    }

    setProductErrors({ ...productErrors, variablePriceError: errorMessage });
  };

  const validateProduct = (validateStartDate: boolean) => {
    let isValid = true;

    const newProductErrors = { ...initialProductErrors };

    if (!product.selectedPaymentMethod) {
      newProductErrors.paymentMethodNotSelectedError = t(
        'customer-product-page.payment-profile-empty-error'
      );
      isValid = false;
    }

    if (
      product.isVariablePrice &&
      product.selectedPaymentMethod !== PaymentMethod.Subscription
    ) {
      if (!selectedVariantTotalPrice) {
        newProductErrors.variablePriceError = totalPriceError;
        isValid = false;
      } else {
        const errorMessage = validateVariablePrice(selectedVariant);
        if (CommonValidation.isNotEmpty(errorMessage)) {
          newProductErrors.variablePriceError = errorMessage;
          isValid = false;
        }
      }
    }

    if (validateStartDate) {
      if (
        CommonValidation.isEmpty(product.policyStartDate) ||
        CommonValidation.isDateNull(product.policyStartDate, true)
      ) {
        newProductErrors.policyStartDateError = t(
          'customer-product-page.policy-date-empty-error'
        );
        isValid = false;
      } else if (!isPolicyStartDateInRange()) {
        if (isQuote === true && product.productTypeId === ProductTypes.Gap) {
          newProductErrors.policyStartDateError = t(
            'customer-product-page.policy-date-limit-gapquote-error',
            { policyStartDateLimit: product.policyStartDateLimit }
          );
        } else {
          newProductErrors.policyStartDateError = t(
            'customer-product-page.policy-date-limit-error',
            { policyStartDateLimit: product.policyStartDateLimit }
          );
        }
        isValid = false;
      } else if (isPastDateSelected(product.policyStartDate)) {
        newProductErrors.policyStartDateError = t(
          'customer-product-page.policy-date-past-error'
        );
        isValid = false;
      }
    }

    if (tyreTypes.length > 0) {
      if (product.selectedTyreTypeId === undefined) {
        newProductErrors.tyreTypeNotSelectedError = t(
          'customer-product-page.tyre-type-empty-error'
        );
        isValid = false;
      }

      if (product.selectedClaimLimit === undefined) {
        newProductErrors.claimLimitNotSelectedError = t(
          'customer-product-page.claim-limit-empty-error'
        );
        isValid = false;
      }
    } else if (
      coverLevels.length > 1 &&
      product.selectedCoverLevelId === undefined
    ) {
      newProductErrors.coverLevelNotSelectedError = t(
        'customer-product-page.cover-level-empty-error'
      );
      isValid = false;
    }

    if (
      selectedVariant === undefined &&
      product.selectedPaymentMethod !== PaymentMethod.Subscription
    ) {
      newProductErrors.variantNotSelectedError = t(
        'customer-product-page.cover-duration-empty-error'
      );
      isValid = false;
    }

    setProductErrors(newProductErrors);
    onValidated(product.productTypeId, isValid);
  };

  const isPolicyStartDateInRange = (): boolean => {
    const minDate = getMinPolicyStartDate();
    const maxDate = getMaxPolicyStartDate();

    const jsonPolicyStartDate = CommonValidation.getJsonDate(
      product.policyStartDate
    );

    if (!jsonPolicyStartDate) {
      return false;
    }

    const policyDate = new Date(jsonPolicyStartDate);

    const result =
      policyDate.setHours(0, 0, 0, 0) >= minDate.setHours(0, 0, 0, 0) &&
      policyDate.setHours(0, 0, 0, 0) <= maxDate.setHours(0, 0, 0, 0);

    return result;
  };

  const isPastDateSelected = (
    policyStartDate: string | undefined,
    includeToday: boolean = false
  ): boolean => {
    if (policyStartDate === undefined) return false;

    const jsonPolicyDate = CommonValidation.getJsonDate(policyStartDate);

    if (!jsonPolicyDate) return false;

    const policyDate = new Date(jsonPolicyDate);
    policyDate.setHours(0, 0, 0, 0);

    const dateToday = new Date();
    dateToday.setHours(0, 0, 0, 0);

    if (includeToday) {
      return policyDate <= dateToday;
    }

    return policyDate < dateToday;
  };

  const renderDuration = () => {
    return durations
      .filter((d) => d.toString() !== '1')
      .map((duration: string, i) => {
        const controlId = `coverduration-${i}-${product.productTypeId}-${product.productSubTypeId}`;

        return (
          <div key={controlId} className='form-check form-check-inline'>
            <input
              className='form-check-input'
              type='radio'
              name={`durationOptions-${controlId}`}
              id={controlId}
              value={selectedVariant?.duration}
              onChange={() => onDurationChange(duration)}
              checked={duration === selectedVariant?.duration}
              disabled={!product.selectedPaymentMethod}
            />
            <label className='form-check-label' htmlFor={controlId}>
              {duration} months
            </label>
          </div>
        );
      });
  };

  const getTyreTypeName = (tyreTypeId: number): string => {
    switch (tyreTypeId) {
      case 1:
        return 'Standard';
      case 2:
        return 'Run flat';
      default:
        return '';
    }
  };

  const renderTyreTypes = (): JSX.Element[] => {
    return tyreTypes.map((tyreTypeId: number, i) => {
      const controlId = `tyreType-${i}-${product.productTypeId}-${product.productSubTypeId}`;

      return (
        <div key={controlId} className='form-check form-check-inline'>
          <input
            className='form-check-input'
            type='radio'
            name={`tyreOptions-${controlId}`}
            id={controlId}
            value={tyreTypeId}
            onChange={() => updateTyreType(tyreTypeId)}
            checked={tyreTypeId === product.selectedTyreTypeId}
          />
          <label className='form-check-label' htmlFor={controlId}>
            {getTyreTypeName(tyreTypeId)}
          </label>
        </div>
      );
    });
  };

  const renderClaimLimits = () => {
    return claimLimits.map((claimLimit: ClaimLimit, i) => {
      const controlId = `claimlimit-${i}-${product.productTypeId}-${product.productSubTypeId}`;

      return (
        <div key={controlId} className='form-check form-check-inline'>
          <input
            className='form-check-input'
            type='radio'
            name={`claimLimitOptions-${controlId}`}
            id={controlId}
            value={claimLimit.value}
            onChange={() => updateClaimLimit(claimLimit)}
            checked={claimLimit.value === product.selectedClaimLimit}
            disabled={tyreTypes.length > 0 && product.selectedTyreTypeId === 0}
          />
          <label className='form-check-label' htmlFor={controlId}>
            {currencySymbol}
            {`${claimLimit.value}`}
          </label>
        </div>
      );
    });
  };

  const renderCoverLevel = () => {
    return coverLevels.map((coverLevel: CoverLevel, i) => {
      const controlId = `coverlevel-${i}-${product.productTypeId}-${product.productSubTypeId}`;

      return (
        <div key={controlId} className='form-check form-check-inline'>
          <input
            className='form-check-input'
            type='radio'
            name={`claimLimitOptions-${controlId}`}
            id={controlId}
            value={coverLevel.value}
            onChange={() => updateCoverLevel(coverLevel)}
            checked={coverLevel.value === product.selectedCoverLevelId}
          />
          <label className='form-check-label' htmlFor={controlId}>
            {coverLevel.text}
          </label>
        </div>
      );
    });
  };

  const getMinPolicyStartDate = (): Date => {
    return new Date();
  };

  const getMaxPolicyStartDate = (): Date => {
    if (isQuote === true && product.productTypeId === ProductTypes.Gap) {
      return new Date();
    }

    if (product.expiryDate === null) {
      const isVehicleDeliveryDateInThePastOrToday = isPastDateSelected(
        vehicleDeliveryDate,
        true
      );

      if (!isVehicleDeliveryDateInThePastOrToday) {
        //return yesterday which will prevent a policy start date from being entered (as min date is today).
        const d = new Date();
        d.setDate(d.getDate() - 1);

        return d;
      }

      const jsonVehicleDate =
        CommonValidation.getJsonDate(vehicleDeliveryDate) || '';
      const deliveryDate = new Date(jsonVehicleDate);
      deliveryDate.setDate(
        deliveryDate.getDate() + product.policyStartDateLimit
      );

      return deliveryDate;
    } else {
      const expiryDate = new Date(product.expiryDate);

      return expiryDate;
    }
  };

  const paymentOptionText =
    paymentProfileLabel ||
    (paymentOptions === true
      ? t('customer-product-page.payment-option')
      : t('customer-product-page.payment-profile'));

  const getSelectedPaymentMethod = () => {
    if (exceedsUpsellMileageLimit) {
      return PaymentMethod.Single;
    } else {
      return product.selectedPaymentMethod ?? '';
    }
  };

  const showMoreItems = cmsProduct.productTypeCustomisation.find(
    (p) => p.productType == product.productTypeId
  )?.seeMoreItems;

  return (
    <div className='container-item'>
      <div className='flex-row space-between mobile-to-column'>
        <div>
          <div className='flex-row justify-center space-between'>
            <h4 className='product-selection-header'>{product.productName}</h4>
            {!hideRemovePlanLink && (
              <ButtonCommand
                name='btn-edit'
                text={t('customer-button.button-remove-text')}
                additionalClassNames='m05-left small'
                onClick={addOrRemovePlan}
              />
            )}
          </div>
        </div>
        <div className='mobile-top-margin'>
          <PriceDisplay
            product={product}
            noOfInstallments={noOfInstallments}
            className='p1'
            selectedLinkedProducts={[]}
            culture={culture}
          />
        </div>
      </div>
      <ButtonCommand
        name='btn-show-more'
        text={t('customer-product-page.show-benefits')}
        additionalClassNames='m05-left small'
        onClick={onShowBenefits}
      />
      <RadioSelection
        show={tyreTypes.length > 0}
        selectionName={t('customer-product-page.select-tyre-type')}
        error={productErrors?.tyreTypeNotSelectedError}
        fontDisabledClass=''>
        {renderTyreTypes()}
      </RadioSelection>
      <RadioSelection
        show={tyreTypes.length > 0}
        selectionName={t('customer-product-page.select-claim-limit')}
        error={productErrors?.claimLimitNotSelectedError}
        fontDisabledClass=''>
        {renderClaimLimits()}
      </RadioSelection>
      <RadioSelection
        show={tyreTypes.length === 0 && coverLevels.length > 1}
        selectionName={t('customer-product-page.select-cover-level')}
        error={productErrors?.coverLevelNotSelectedError}
        fontDisabledClass=''>
        {renderCoverLevel()}
      </RadioSelection>
      <div className='flex-row m05-bottom mobile-to-column'>
        <div
          className={
            paymentMethods.length > 1
              ? 'product-payment-method-box'
              : 'product-payment-method-box-small'
          }>
          <div className='control-label m05-bottom'>{paymentOptionText}</div>
          <PaymentMethods
            productTypeId={product.productTypeId}
            paymentMethods={paymentMethods}
            selectedPaymentMethod={getSelectedPaymentMethod()}
            selectedCoverLevel={product.selectedCoverLevelId}
            selectedClaimLimit={product.selectedClaimLimit}
            hasError={
              productErrors &&
              productErrors.paymentMethodNotSelectedError !== ''
            }
            onPaymentMethodClick={onPaymentMethodClick}
          />
          {productErrors &&
            productErrors.paymentMethodNotSelectedError !== '' && (
              <div className='error-style'>
                {productErrors.paymentMethodNotSelectedError}
              </div>
            )}
        </div>
        <div>
          <div className='control-label m05-bottom'>
            {t('customer-product-page.policy-start-date')}
          </div>
          <div className='control'>
            <DateInput
              name='policy-start-date'
              id='policy-start-date'
              className='input-text small'
              useJson
              forceTimeReset
              value={product.policyStartDate}
              error={productErrors?.policyStartDateError || ''}
              onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                onChangePolicyStartDateValue(e)
              }
              minimumDate={getMinPolicyStartDate()}
              maximumDate={useMemo(
                () => getMaxPolicyStartDate(),
                [
                  product.expiryDate,
                  product.productTypeId,
                  product.policyStartDateLimit,
                  vehicleDeliveryDate,
                  isQuote
                ]
              )}
              readOnly={isSubscription}
              disabled={isSubscription}
              culture={culture}
            />
          </div>
        </div>
      </div>
      <RadioSelection
        show={!isSubscription}
        selectionName={t('customer-product-page.select-cover-duration')}
        error={productErrors?.variantNotSelectedError}
        fontDisabledClass={fontDisabledClass}>
        {renderDuration()}
      </RadioSelection>
      <ShowBenefitsModal
        showModal={showModal}
        title={product.productName}
        showMoreItems={showMoreItems}
        onCloseModal={onCloseModal}
      />
      {showPriceField && !isSubscription && (
        <div className='m05-bottom'>
          <div className={`control-label m05-bottom ${fontDisabledClass}`}>
            {t('customer-product-page.total-price')}
          </div>
          <div className='control price-control'>
            {product.isVariablePrice ? (
              <PriceInput
                name='totalPrice'
                value={totalPrice}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangeTotalPrice(e)
                }
                onLeave={(e: React.FormEvent<HTMLInputElement>) =>
                  onLeaveTotalPrice(e)
                }
                onFocus={() => onPriceFocus()}
                currency={currencySymbol}
                placeholder={t('customer-product-page.total-price')}
                readonly={!product.selectedPaymentMethod}
                error={productErrors?.variablePriceError ?? ''}
                culture={culture}
              />
            ) : (
              <PriceInput
                name='fixed-total-price'
                value={selectedVariantTotalPrice?.toString() ?? ''}
                currency={currencySymbol}
                placeholder={t('customer-product-page.total-price')}
                readonly
                culture={culture}
              />
            )}
          </div>
        </div>
      )}
      <hr />
    </div>
  );
};

export default ProductSelection;
